import { useCallback } from 'react';

import { chronicleBridge } from '@/tauri/chronicle';
import type { ChronicleError } from '@/tauri/chronicle/types';
import {
  trackHardwareBluetoothPermissionResult,
  trackHardwareBluetoothSettingsOpened,
} from '@/utils/analytics/track-hardware-events';
import { chronicleLogger as logger } from '@/utils/logger/chronicle-logger';

import { assertChronicleBlePrereqs } from './utils/assertChronicleBlePrereqs';

export type BluetoothCheckResult = { ok: true } | { ok: false; error: ChronicleError };

/**
 * Hook for checking Bluetooth availability and permissions.
 * Provides utilities for Bluetooth status checks before device operations.
 */
export function useBluetoothCheck() {
  /**
   * Check if Bluetooth is available and has required permissions.
   * Denied-only permission hard-stop; prompt_required / unknown fall through
   * so native scan can still raise the system Bluetooth prompt. Radio must be on.
   */
  const checkBluetoothStatus = useCallback(async (): Promise<BluetoothCheckResult> => {
    const result = await assertChronicleBlePrereqs();
    if (!result.ok) {
      const code = result.error.code;
      if (code === 'BLUETOOTH_PERMISSION_DENIED') {
        // Denied is the only status that returns this code.
        trackHardwareBluetoothPermissionResult(result.permissionStatus ?? 'denied');
      } else if (code === 'BLUETOOTH_DISABLED') {
        trackHardwareBluetoothPermissionResult('poweredOff');
      } else {
        trackHardwareBluetoothPermissionResult('unknown');
      }
      return { ok: false, error: result.error };
    }

    // Ok can be a real grant or prompt_required/unknown fall-through — don't
    // count NotDetermined as authorized.
    const ps = result.status.permissionStatus;
    trackHardwareBluetoothPermissionResult(
      ps === 'prompt_required' || ps === 'unknown' ? ps : 'authorized'
    );
    return { ok: true };
  }, []);

  /**
   * Open the system Bluetooth settings panel (for turning Bluetooth on/off).
   * Use this when Bluetooth is disabled.
   */
  const openBluetoothSettings = useCallback(async () => {
    if (!chronicleBridge.isAvailable()) {
      return;
    }

    try {
      trackHardwareBluetoothSettingsOpened();
      await chronicleBridge.openBluetoothSettings();
    } catch (error) {
      logger.error({
        msg: 'Failed to open Bluetooth settings',
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }, []);

  /**
   * Open the system Privacy & Security → Bluetooth settings panel.
   * Use this when the user needs to grant Bluetooth permission to the app.
   */
  const openBluetoothPrivacySettings = useCallback(async () => {
    if (!chronicleBridge.isAvailable()) {
      return;
    }

    try {
      trackHardwareBluetoothSettingsOpened();
      await chronicleBridge.openBluetoothPrivacySettings();
    } catch (error) {
      logger.error({
        msg: 'Failed to open Bluetooth privacy settings',
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }, []);

  return {
    checkBluetoothStatus,
    openBluetoothSettings,
    openBluetoothPrivacySettings,
  };
}
